import { useState, useEffect, useCallback } from 'react';
import {
  VendorLookupItem,
  FamilyLookupItem,
  SubFamilyLookupItem,
  BarcodeType1LookupItem,
  BarcodeType2LookupItem,
  CurrencyLookupItem,
  UnitOfMeasureLookupItem,
  Surcharge, // Assuming Surcharge is sufficient for lookup
  ProductLookupItem,
  CompositeLookupItem,
  ProcessLookupItem,
} from '../types/costwand';
import { apiClient, ApiError } from '../api/apiClient.ts';

// Define a generic lookup item structure for internal use
interface GenericLookupItem {
  id: number;
  description: string; // Or name for UnitOfMeasure, etc.
}

interface AllLookups {
  vendors: VendorLookupItem[];
  families: FamilyLookupItem[];
  subFamilies: SubFamilyLookupItem[];
  barcodeType1s: BarcodeType1LookupItem[];
  barcodeType2s: BarcodeType2LookupItem[];
  currencies: CurrencyLookupItem[];
  unitsOfMeasure: UnitOfMeasureLookupItem[];
  surcharges: Surcharge[];
  products: ProductLookupItem[];
  composites: CompositeLookupItem[];
  processes: ProcessLookupItem[];
  loading: boolean;
  error: string | null;
  resolveId: (tableName: string, id: number | string | null | undefined) => string;
}

export const useAllLookups = (): AllLookups => {
  const [lookupData, setLookupData] = useState<Omit<AllLookups, 'loading' | 'error' | 'resolveId'>>({
    vendors: [],
    families: [],
    subFamilies: [],
    barcodeType1s: [],
    barcodeType2s: [],
    currencies: [],
    unitsOfMeasure: [],
    surcharges: [],
    products: [],
    composites: [],
    processes: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchAllLookups = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [
        vendorsResponse,
        familiesResponse,
        subFamiliesResponse,
        barcodeType1sResponse,
        barcodeType2sResponse,
        currenciesResponse,
        unitsOfMeasureResponse,
        surchargesResponse,
        productsResponse,
        compositesResponse,
        processesResponse,
      ] = await Promise.all([
        apiClient<{ data: VendorLookupItem[] }>('/vendors', { allPages: true }),
        apiClient<{ data: FamilyLookupItem[] }>('/families', { allPages: true }),
        apiClient<{ data: SubFamilyLookupItem[] }>('/subfamilies', { allPages: true }),
        apiClient<{ data: BarcodeType1LookupItem[] }>('/barcode_1', { allPages: true }),
        apiClient<{ data: BarcodeType2LookupItem[] }>('/barcode_2', { allPages: true }),
        apiClient<{ data: CurrencyLookupItem[] }>('/currencies', { allPages: true }),
        apiClient<{ data: UnitOfMeasureLookupItem[] }>('/units_of_measure', { allPages: true }),
        apiClient<{ data: Surcharge[] }>('/surcharges', { allPages: true }),
        apiClient<{ data: ProductLookupItem[] }>('/products/lookup', { allPages: true }),
        apiClient<{ data: CompositeLookupItem[] }>('/composites/lookup', { allPages: true }),
        apiClient<{ data: ProcessLookupItem[] }>('/processes', { allPages: true }),
      ]);

      setLookupData({
        vendors: vendorsResponse.data,
        families: familiesResponse.data,
        subFamilies: subFamiliesResponse.data,
        barcodeType1s: barcodeType1sResponse.data,
        barcodeType2s: barcodeType2sResponse.data,
        currencies: currenciesResponse.data,
        unitsOfMeasure: unitsOfMeasureResponse.data,
        surcharges: surchargesResponse.data,
        products: productsResponse.data,
        composites: compositesResponse.data,
        processes: processesResponse.data,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        setError(`Failed to fetch lookup data: ${err.message}`);
      } else {
        setError('An unknown error occurred while fetching lookup data.');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchAllLookups();
  }, [fetchAllLookups]);

  const resolveId = useCallback((tableName: string, id: number | string | null | undefined): string => {
    if (id === null || id === undefined || id === '') {
      return 'N/A';
    }

    const numericId = typeof id === 'string' ? parseInt(id, 10) : id;
    if (isNaN(numericId)) {
      return String(id); // Return original if not a valid number
    }

    switch (tableName) {
      case 'vendors':
        return lookupData.vendors.find(item => item.id === numericId)?.name || `Vendor ID: ${numericId}`;
      case 'families':
        return lookupData.families.find(item => item.id === numericId)?.description || `Family ID: ${numericId}`;
      case 'subfamilies':
        return lookupData.subFamilies.find(item => item.id === numericId)?.description || `Subfamily ID: ${numericId}`;
      case 'barcode_1':
        return lookupData.barcodeType1s.find(item => item.id === numericId)?.description || `Barcode 1 ID: ${numericId}`;
      case 'barcode_2':
        return lookupData.barcodeType2s.find(item => item.id === numericId)?.description || `Barcode 2 ID: ${numericId}`;
      case 'currencies':
        return lookupData.currencies.find(item => item.id === numericId)?.description || `Currency ID: ${numericId}`;
      case 'units_of_measure':
        return lookupData.unitsOfMeasure.find(item => item.id === numericId)?.name || `UoM ID: ${numericId}`;
      case 'surcharges':
        return lookupData.surcharges.find(item => item.id === numericId)?.description || `Surcharge ID: ${numericId}`;
      case 'products': // For product_id in discounts, or component_id if it's a product
        // Combine products and composites for component lookup
        const combinedProductLookup = [...lookupData.products, ...lookupData.composites];
        return combinedProductLookup.find(item => item.id === numericId)?.description || `Product/Composite ID: ${numericId}`;
      case 'composites': // For component_id if it's a composite
        return lookupData.composites.find(item => item.id === numericId)?.description || `Composite ID: ${numericId}`;
      case 'processes': // For process_id in compositions
        return lookupData.processes.find(item => item.id === numericId)?.description || `Process ID: ${numericId}`;
      default:
        return String(id);
    }
  }, [lookupData]);

  return {
    ...lookupData,
    loading,
    error,
    resolveId,
  };
};